import { useState, useMemo } from "react";
import { useUserModule } from "../../../contexts/UserModuleContext";
import { useEquipmentContext } from "../../../contexts/EquipmentContext";
import { groupTasksByCategory, formatEvaluationDateDisplay } from "../../../utils/userModuleUtils";
import { getSkillLevelColor } from "../../../utils/skillLevelColors";
import { Card } from "../../ui";
import { SkillsetClassificationEvaluations } from "../SkillsetClassificationEvaluations";
import type { SkillCategory } from "../../../types/userModuleTypes";
import "../../../css/UserProfileTabs.css";

interface SkillsetsTabProps {
    user: any;
    readOnly: boolean;
}

/**
 * SkillsetsTab — Displays user's skill qualifications.
 * Admin view: classification evaluations from the User Skillsets library, with grading.
 * Employee view: read-only with "My Qualifications" header and info banner.
 */
export const SkillsetsTab = ({
    user,
    readOnly,
}: SkillsetsTabProps) => {
    const {
        classifications,
        categories,
        tasks,
        subCategories,
        skillAssignments,
        skillLevels,
    } = useUserModule();
    const { companyUnits } = useEquipmentContext();

    // Category pill filter state — when a pill is clicked, filter tasks to only those matching that subCategoryId
    const [filterSubCategoryByCategory, setFilterSubCategoryByCategory] = useState<
        Record<string, string | null>
    >({});

    // Get assignments for this user
    const userAssignments = useMemo(
        () => skillAssignments.filter((a) => a.userId === user?.id),
        [skillAssignments, user?.id]
    );

    // Group tasks by category
    const tasksByCategory = useMemo(
        () => groupTasksByCategory(tasks, categories),
        [tasks, categories]
    );

    // Determine which classifications have assignments for this user
    const assignedClassificationIds = useMemo(() => {
        const taskIds = new Set(userAssignments.map((a) => a.taskId));
        const categoryIds = new Set(
            tasks.filter((t) => taskIds.has(t.id)).map((t) => t.categoryId)
        );
        return new Set(
            categories.filter((c) => categoryIds.has(c.id)).map((c) => c.classificationId)
        );
    }, [userAssignments, tasks, categories]);

    // Qualification summary stats
    const stats = useMemo(() => {
        const totalClassifications = assignedClassificationIds.size;
        const assignedCategoryIds = new Set(
            categories
                .filter((c) => assignedClassificationIds.has(c.classificationId))
                .map((c) => c.id)
        );
        const totalCategories = assignedCategoryIds.size;
        const totalTasks = userAssignments.length;

        // Skill level counts for employee view
        const levelCounts: Record<string, number> = {};
        userAssignments.forEach((a) => {
            const level = a.skillLevel || "Not Evaluated";
            levelCounts[level] = (levelCounts[level] || 0) + 1;
        });

        // Last updated date (most recent evaluation date)
        let lastUpdated: string | null = null;
        let lastEvaluator: string | null = null;
        userAssignments.forEach((a) => {
            if (a.evaluationDate && (!lastUpdated || a.evaluationDate > lastUpdated)) {
                lastUpdated = a.evaluationDate;
                lastEvaluator = a.evaluatedByName || null;
            }
        });

        return { totalClassifications, totalCategories, totalTasks, levelCounts, lastUpdated, lastEvaluator };
    }, [assignedClassificationIds, categories, userAssignments]);

    // Get sub-category options for a skill category
    const getSubCategoryPills = (category: SkillCategory): { id: string; name: string }[] => {
        const categoryTasks = tasks.filter((t) => t.categoryId === category.id);
        const categoryTaskIds = new Set(categoryTasks.map((t) => t.id));
        const assignedSubIds = new Set(
            userAssignments
                .filter((a) => categoryTaskIds.has(a.taskId) && a.subCategoryId)
                .map((a) => a.subCategoryId!)
        );
        if (assignedSubIds.size === 0) return [];

        // Build a lookup from all possible sources: equipment units + manual sub-categories
        const results: { id: string; name: string }[] = [];
        for (const subId of assignedSubIds) {
            // Try equipment units
            if (category.equipmentCategoryId) {
                const unit = companyUnits.find((u) => u.id === subId);
                if (unit) { results.push({ id: subId, name: unit.name || subId }); continue; }
            }
            // Try manual sub-categories
            const subCat = subCategories.find((sc) => sc.id === subId);
            if (subCat) { results.push({ id: subId, name: subCat.name }); continue; }
            // Fallback — show ID (shouldn't happen)
            results.push({ id: subId, name: subId });
        }
        return results;
    };

    // Get machine # pills for a specific task (from the user's assignments for that task)
    const getTaskMachineNumberPills = (taskId: string, category: SkillCategory): { id: string; name: string }[] => {
        const taskAssignments = userAssignments.filter((a) => a.taskId === taskId && a.subCategoryId);
        if (taskAssignments.length === 0) return [];

        // Dedupe by subCategoryId
        const seen = new Set<string>();
        const results: { id: string; name: string }[] = [];
        for (const a of taskAssignments) {
            const subId = a.subCategoryId!;
            if (seen.has(subId)) continue;
            seen.add(subId);
            // Try equipment units
            if (category.equipmentCategoryId) {
                const unit = companyUnits.find((u) => u.id === subId);
                if (unit) { results.push({ id: subId, name: unit.name || subId }); continue; }
            }
            // Try sub-categories
            const subCat = subCategories.find((sc) => sc.id === subId);
            if (subCat) { results.push({ id: subId, name: subCat.name }); continue; }
            // Fallback
            results.push({ id: subId, name: subId });
        }
        return results;
    };

    // Get skill level color set from the utility (uses company's skillLevels data)
    const getLevelColor = (level: string | null) => getSkillLevelColor(level, skillLevels);

    // Get the dot color for a skill level
    const getSkillDotColor = (level: string | null): string => getLevelColor(level).dot;

    // Format date for display (timezone-safe calendar date)
    const formatDate = (dateStr: string | null | undefined): string =>
        formatEvaluationDateDisplay(dateStr);

    // ===== EMPLOYEE READ-ONLY VIEW =====
    if (readOnly) {
        return (
            <div className="upt-skillsets-tab">
                {/* Info Banner */}
                <div className="upt-skillsets-info-banner">
                    <span className="upt-skillsets-info-icon">ℹ️</span>
                    <span>Your skillset record is maintained by your supervisor. Contact your admin to request changes.</span>
                </div>

                {/* My Qualifications Summary */}
                <div className="upt-skillsets-summary-header">
                    <h3>My Qualifications</h3>
                    {stats.lastUpdated && (
                        <span className="upt-skillsets-last-updated">
                            Last updated {formatDate(stats.lastUpdated)} by {stats.lastEvaluator || "—"}
                        </span>
                    )}
                </div>
                <div className="upt-skillsets-summary">
                    <div className="upt-skillsets-stat-card">
                        <div className="upt-skillsets-stat-label">CLASSIFICATIONS</div>
                        <div className="upt-skillsets-stat-value">{stats.totalClassifications}</div>
                    </div>
                    <div className="upt-skillsets-stat-card">
                        <div className="upt-skillsets-stat-label">TASKS ASSIGNED</div>
                        <div className="upt-skillsets-stat-value">{stats.totalTasks}</div>
                    </div>
                    <div className="upt-skillsets-stat-card">
                        <div className="upt-skillsets-stat-label">SKILL LEVELS</div>
                        <div className="upt-skillsets-level-summary">
                            {Object.entries(stats.levelCounts)
                                .filter(([level]) => level.toLowerCase() !== "not evaluated")
                                .map(([level, count]) => (
                                    <span key={level} className="upt-level-chip" style={{ background: getLevelColor(level).bg, color: getLevelColor(level).text }}>
                                        {level} ×{count}
                                    </span>
                                ))}
                        </div>
                    </div>
                </div>

                {/* Classifications - Read Only */}
                <div className="upt-skillsets-list">
                    {classifications
                        .filter((c) => assignedClassificationIds.has(c.id))
                        .map((classification) => {
                            const classCategories = categories.filter((c) => c.classificationId === classification.id);
                            return (
                                <Card
                                    key={classification.id}
                                    variant="card-sm"
                                    className="card-pad-0 upt-skillsets-classification-card"
                                >
                                    <div className="upt-skillsets-classification-header-v2">
                                        {classification.name}
                                    </div>
                                    <div className="upt-skillsets-classification-body">
                                        {classCategories.map((category) => {
                                            const categoryTasks = tasksByCategory.get(category.id) || [];
                                            const assignedTasks = categoryTasks.filter((t) =>
                                                userAssignments.some((a) => a.taskId === t.id)
                                            );
                                            if (assignedTasks.length === 0) return null;

                                            const pills = getSubCategoryPills(category);

                                            return (
                                                <div key={category.id} className="upt-skillsets-category-section">
                                                    <div className="upt-skillsets-category-header-v2">
                                                        <span className="upt-skillsets-category-name-v2">{category.name}</span>
                                                        {pills.length > 0 && (
                                                            <div
                                                                className="upt-skillsets-pills"
                                                                role="group"
                                                                aria-label={`${category.name} machine filters`}
                                                            >
                                                                {(filterSubCategoryByCategory[category.id] ?? null) && (
                                                                    <button
                                                                        type="button"
                                                                        className="badge badge-neutral btn-sm upt-skillsets-show-all"
                                                                        onClick={() =>
                                                                            setFilterSubCategoryByCategory((prev) => ({
                                                                                ...prev,
                                                                                [category.id]: null,
                                                                            }))
                                                                        }
                                                                    >
                                                                        Show all
                                                                    </button>
                                                                )}
                                                                {pills.map((p) => {
                                                                    const activeFilter =
                                                                        filterSubCategoryByCategory[category.id] ??
                                                                        null;
                                                                    return (
                                                                        <button
                                                                            key={p.id}
                                                                            type="button"
                                                                            className={`badge badge-neutral btn-sm${activeFilter === p.id ? " is-active" : ""}`}
                                                                            aria-pressed={activeFilter === p.id}
                                                                            onClick={() =>
                                                                                setFilterSubCategoryByCategory(
                                                                                    (prev) => ({
                                                                                        ...prev,
                                                                                        [category.id]:
                                                                                            prev[category.id] === p.id
                                                                                                ? null
                                                                                                : p.id,
                                                                                    }),
                                                                                )
                                                                            }
                                                                        >
                                                                            {p.name}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {assignedTasks.map((task) => {
                                                        const assignment = userAssignments.find((a) => a.taskId === task.id);
                                                        const level = assignment?.skillLevel || "Not Evaluated";
                                                        const taskPills = getTaskMachineNumberPills(task.id, category);
                                                        const activeMachineFilter =
                                                            filterSubCategoryByCategory[category.id] ?? null;
                                                        // Apply filter only within this category
                                                        if (
                                                            activeMachineFilter &&
                                                            !taskPills.some((pill) => pill.id === activeMachineFilter)
                                                        )
                                                            return null;
                                                        return (
                                                            <div key={task.id} className="upt-skillsets-task-row-v2 readonly">
                                                                <div className="upt-skillsets-task-left">
                                                                    <span className="upt-skillsets-task-name-v2">{task.name}</span>
                                                                    {taskPills.length > 0 && (
                                                                        <div className="upt-skillsets-pills inline">
                                                                            {taskPills.map((p) => (
                                                                                <span key={p.id} className="badge badge-neutral btn-sm">{p.name}</span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="upt-skillsets-task-eval-stacked">
                                                                    <span className="upt-skillsets-level-badge-v2" style={{ background: getLevelColor(level).bg, color: getLevelColor(level).text }}>
                                                                        <span className="upt-dot" style={{ background: getSkillDotColor(level) }} />
                                                                        {level}
                                                                    </span>
                                                                    {assignment?.evaluationDate && assignment.skillLevel !== "Not Evaluated" && (
                                                                        <span className="upt-skillsets-eval-info">
                                                                            {formatDate(assignment.evaluationDate)} · {assignment.evaluatedByName || "—"}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>
                            );
                        })}
                </div>
            </div>
        );
    }

    // ===== ADMIN VIEW =====
    return (
        <div className="upt-skillsets-tab">
            {/* Qualification Summary */}
            <div className="upt-skillsets-summary-header">
                <h3>Qualification Summary</h3>
                <span className="upt-skillsets-summary-meta">
                    {stats.totalClassifications} classifications · {stats.totalCategories} categories · {stats.totalTasks} tasks assigned
                </span>
            </div>
            <div className="upt-skillsets-summary">
                <div className="upt-skillsets-stat-card">
                    <div className="upt-skillsets-stat-label">CLASSIFICATIONS</div>
                    <div className="upt-skillsets-stat-value">{stats.totalClassifications}</div>
                </div>
                <div className="upt-skillsets-stat-card">
                    <div className="upt-skillsets-stat-label">CATEGORIES</div>
                    <div className="upt-skillsets-stat-value">{stats.totalCategories}</div>
                </div>
                <div className="upt-skillsets-stat-card">
                    <div className="upt-skillsets-stat-label">TASKS</div>
                    <div className="upt-skillsets-stat-value">{stats.totalTasks}</div>
                </div>
                <div className="upt-skillsets-stat-card">
                    <div className="upt-skillsets-stat-label">LAST UPDATED</div>
                    <div className="upt-skillsets-stat-value-sm">
                        {stats.lastUpdated ? formatDate(stats.lastUpdated) : "—"}
                    </div>
                </div>
            </div>

            <SkillsetClassificationEvaluations userId={user?.id || ""} />

            {/* Footer note */}
            <p className="upt-skillsets-footer-note">
                Every change to this record is automatically logged with the date and name of the evaluator.
            </p>
        </div>
    );
};
